import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import {
  Text,
  Flex,
  Stack,
  Button,
  FormControl,
  FormLabel,
  FormErrorMessage,
  Input,
  useColorModeValue
} from "@chakra-ui/react";
import Card from "components/Card/Card";
import CardBody from "components/Card/CardBody";
import CardHeader from "components/Card/CardHeader";
import Loading from "components/Loading/Loading";
import { useCustomToast } from "components/CustomToast/CustomToast";
import {
  checkErrors,
  hasError,
} from "utils/forms-utils";
import { validateParameterizedDate } from "utils/traffic-violation-utils";
import { trafficViolationService } from "services/trafficViolationService";
import moment from "moment";
import "moment/locale/pt-br";
import "react-datetime/css/react-datetime.css";
import DateTime from "react-datetime";
import { DATE_FORMAT, LOCALE_PT_BR } from "constants/date-time";


const TrafficViolationPaymentForm = () => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState(undefined);
  const [inputError, setInputError] = useState({});

  const navigate = useNavigate();
  const showToast = useCustomToast();
  const textColor = useColorModeValue("gray.700", "white");

  const ids = useMemo(() => ({
    bankCode: "bank-code",
    paymentDate: "payment-date",
    paidValue: "paid-value",
    creditDate: "credit-date",
    agencyCode: "agency-code",
  }), []);
  const isFormInvalid = formData && inputError ? hasError({ formData, inputError }) : true;
  const isSaveDisabled = isFormInvalid;
  const cardSpacing = "20px";
  const translationKey = "traffic-violation";
  const formTranslationKey = `${translationKey}.form`;

  const initFormData = () => {
    return {
      [ids.bankCode]: {
        value: "",
        validations: [
          { required: true },
        ],
      },
      [ids.paymentDate]: {
        value: "",
        validations: [
          {
            callback: ({ values }) =>
              validateParameterizedDate(values, ids, "system.messages.errors.required-field", ids.paymentDate),
          }
        ]
      },
      [ids.paidValue]: {
        value: "",
        validations: [{ required: true }],
      },
      [ids.creditDate]: {
        value: "",
        validations: [
          {
            callback: ({ values }) =>
              validateParameterizedDate(values, ids,  null, ids.creditDate, false),
          }
        ]
      },
      [ids.agencyCode]: {
        value: "",
        validations: [],
      },
    };
  }

  const handleInputChange = (e) => {
    const { id, type } = e.target;
    const value = type === "checkbox" ? e.target.checked : e.target.value;

    const newFormData = { ...formData, [id]: { ...formData[id], value: value } };
    setFormData(newFormData);
    setInputError(checkErrors({ formData: newFormData, inputError, name: id }));
  };

  const redirectToDetails = () => {
    navigate(-1);
  }


  const fetchData = () => {
    try {
      const initialForm = initFormData();
      setFormData(initialForm);
    } catch (err) {
      showToast({ title: t(err), status: "error" });
      redirectToDetails();
    }

  }

  useEffect(() => {
    fetchData();
  }, []);

  const toDTO = () => ({
    codigo_banco: formData[ids.bankCode].value.trim(),
    data_pagamento: formData[ids.paymentDate].value.trim(),
    valor_pago: formData[ids.paidValue].value.trim(),
    data_credito: formData[ids.creditDate].value.trim(),
    codigo_agencia: formData[ids.agencyCode].value.trim(),
  });

  const handleAitPaymentDateChange = (value) => {
      console.log(value);
      const m = moment(value);
      const date = m.isValid() ? m.format("DD/MM/YYYY") : "";
      console.log(date);
      const updated = {
        ...formData,
        [ids.paymentDate]: {
          ...formData[ids.paymentDate],
          value: date,
        },
      };
  
      setFormData(updated);
  
      setInputError((prev) =>
        checkErrors({ formData: updated, inputError: prev, name: ids.paymentDate })
      );
    };

    const handleAitCreditDateChange = (value) => {
      const m = moment(value);
      const date = m.isValid() ? m.format("DD/MM/YYYY") : value;
  
      const updated = {
        ...formData,
        [ids.creditDate]: {
          ...formData[ids.creditDate],
          value: date,
        },
      };
  
      setFormData(updated);
  
      setInputError((prev) =>
        checkErrors({ formData: updated, inputError: prev, name: ids.creditDate })
      );
    };

  const toFormData = () => {
    const formValues = toDTO()
    return formValues;
  };

  const handleSave = async () => {
    try {
      const data = toFormData();
      const urlFromCurrentTrafficViolation = document.URL;
      const idFromUrl = urlFromCurrentTrafficViolation.substring(urlFromCurrentTrafficViolation.lastIndexOf('/')+1);
      console.log(idFromUrl)
      await trafficViolationService.confirmPayment(idFromUrl, data);
      showToast({ title: t("traffic-violation.messages.success.create"), status: "success" });
      redirectToDetails();
    } catch (err) {
      showToast({ title: t(err), status: "error" });
    }
  }

  return (
    <Flex gap="5" direction="column" minH="89vh" align="center" pt={{ sm: "150px", md: "90px" }}>
      <Card px="0px" mb="24px">
        <CardHeader px="22px" mb="24px">
          <Flex direction="column">
            <Text color={textColor} fontSize="lg" mb="6px">
              <Text as="span" fontWeight="normal">
                {t("traffic-violation.list.table.actions.confirm-payment")}
              </Text>{"\u00A0"}
              <Text as="span" fontWeight="bold">

              </Text>
            </Text>
          </Flex>
        </CardHeader>

        <CardBody px="22px">
          {formData ? (
            <Stack w="100%" direction="column" spacing={cardSpacing}>
              <Stack spacing={cardSpacing} direction="column">
                <FormControl isRequired isInvalid={inputError[ids.bankCode]}>
                  <FormLabel>{t(`${formTranslationKey}.fields.bank-code.label`)}</FormLabel>
                  <Input
                    fontSize="sm"
                    variant="main"
                    id={ids.bankCode}
                    value={formData[ids.bankCode].value}
                    onChange={handleInputChange}
                    placeholder={t(`${formTranslationKey}.fields.bank-code.placeholder`)}
                  />
                  <FormErrorMessage>
                    {t(inputError[ids.bankCode])}
                  </FormErrorMessage>
                </FormControl>
                <FormControl isRequired isInvalid={inputError[ids.paymentDate]}>
                  <FormLabel>{t(`${formTranslationKey}.fields.payment-date.label`)}</FormLabel>
                  <DateTime
                    dateFormat={DATE_FORMAT}
                    timeFormat={false}
                    locale={LOCALE_PT_BR}
                    value={moment(formData[ids.paymentDate].value)}
                    renderInput={(props) => (<Input {...props} fontSize="sm" variant="main" />)}
                    onChange={handleAitPaymentDateChange}
                  />
                  <FormErrorMessage>
                    {t(inputError[ids.paymentDate])}
                  </FormErrorMessage>
                </FormControl>
                <FormControl isRequired isInvalid={inputError[ids.paidValue]}>
                  <FormLabel>{t(`${formTranslationKey}.fields.paid-value.label`)}</FormLabel>
                  <Input
                    fontSize="sm"
                    variant="main"
                    id={ids.paidValue}
                    value={formData[ids.paidValue].value}
                    onChange={handleInputChange}
                    placeholder={t(`${formTranslationKey}.fields.paid-value.placeholder`)}
                  />
                  <FormErrorMessage>
                    {t(inputError[ids.paidValue])}
                  </FormErrorMessage>
                </FormControl>
                <FormControl isInvalid={inputError[ids.creditDate]}>
                  <FormLabel>{t(`${formTranslationKey}.fields.credit-date.label`)}</FormLabel>
                  <DateTime
                    dateFormat={DATE_FORMAT}
                    timeFormat={false}
                    locale={LOCALE_PT_BR}
                    value={moment(formData[ids.creditDate].value)}
                    renderInput={(props) => (<Input {...props} fontSize="sm" variant="main" />)}
                    onChange={handleAitCreditDateChange}
                  />
                  <FormErrorMessage>
                    {t(inputError[ids.creditDate])}
                  </FormErrorMessage>
                </FormControl>
                <FormControl>
                  <FormLabel>{t(`${formTranslationKey}.fields.agency-code.label`)}</FormLabel>
                  <Input
                    fontSize="sm"
                    variant="main"
                    id={ids.agencyCode}
                    value={formData[ids.agencyCode].value}
                    onChange={handleInputChange}
                    placeholder={t(`${formTranslationKey}.fields.agency-code.placeholder`)}
                  />
                  <FormErrorMessage>
                  </FormErrorMessage>
                </FormControl>
                <Stack w="100%" direction="row" justify="space-between">
                  <Button w="125px" variant="no-effects" bg="gray.100" color="gray.700" fontSize="sm" fontWeight="bold" onClick={redirectToDetails}>
                    <Text>
                      {t("system.actions.cancel")}
                    </Text>
                  </Button>

                  <Button
                    w="125px"
                    variant="dark"
                    fontSize="sm"
                    isDisabled={isSaveDisabled}
                    onClick={handleSave}
                    _hover={isSaveDisabled ? {} : undefined}
                  >
                    <Text>
                      {t("system.actions.save")}
                    </Text>
                  </Button>
                </Stack>
              </Stack>
            </Stack>
          ) : (
            <Loading h="300px" w="100%" />
          )}
        </CardBody>
      </Card>
    </Flex>
  );
}

export default TrafficViolationPaymentForm;
